// Argus — AgentSidebar: top sticky search + bulk toolbar + lista cards sortată online-first.

import { useMemo, useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Typography,
  Skeleton,
  Stack,
  Alert,
  Button,
  Snackbar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ClearIcon from '@mui/icons-material/Clear';
import { useQuery } from '@tanstack/react-query';
import { fetchAgents, type Agent } from '../../lib/api';
import { useSidebarStore } from '../../store/sidebarStore';
import { bulkScreenshot } from '../../lib/bulkActions';
import { AgentCard } from './AgentCard';

const AGENTS_POLL_INTERVAL_MS = 5_000;

export interface AgentSidebarProps {
  onAgentSelect: (agentId: string) => void;
  onQuickScreenshot: (agent: Agent) => void;
  onQuickFileExplorer: (agent: Agent) => void;
  onQuickLivestream: (agent: Agent) => void;
}

export function AgentSidebar({
  onAgentSelect,
  onQuickScreenshot,
  onQuickFileExplorer,
  onQuickLivestream,
}: AgentSidebarProps): JSX.Element {
  const search = useSidebarStore((s) => s.search);
  const setSearch = useSidebarStore((s) => s.setSearch);
  const selectedAgentId = useSidebarStore((s) => s.selectedAgentId);
  const bulkSelectedIds = useSidebarStore((s) => s.bulkSelectedIds);
  const toggleBulkSelect = useSidebarStore((s) => s.toggleBulkSelect);
  const clearBulkSelection = useSidebarStore((s) => s.clearBulkSelection);

  const [bulkRunning, setBulkRunning] = useState(false);
  const [snack, setSnack] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const { data: agents, isLoading, error } = useQuery({
    queryKey: ['agents'],
    queryFn: fetchAgents,
    refetchInterval: AGENTS_POLL_INTERVAL_MS,
    refetchOnWindowFocus: false,
  });

  const filteredAndSorted = useMemo(() => {
    if (!agents) return [];
    const term = search.trim().toLowerCase();
    const filtered = term
      ? agents.filter((a) => {
          const fields = [a.pcName, a.windowsUsername, a.hostname, a.lastSeenUser, a.id]
            .filter((s): s is string => Boolean(s))
            .map((s) => s.toLowerCase());
          return fields.some((f) => f.includes(term));
        })
      : agents;

    return [...filtered].sort((a, b) => {
      // Online primii
      if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;
      // Apoi alfabetic windows_username (fallback hostname)
      const nameA = (a.windowsUsername || a.pcName || a.hostname).toLowerCase();
      const nameB = (b.windowsUsername || b.pcName || b.hostname).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [agents, search]);

  const onlineCount = filteredAndSorted.filter((a) => a.isOnline).length;
  const offlineCount = filteredAndSorted.length - onlineCount;
  const bulkCount = bulkSelectedIds.size;

  async function handleBulkScreenshot(): Promise<void> {
    if (bulkRunning || bulkCount === 0) return;
    setBulkRunning(true);
    try {
      const ids = Array.from(bulkSelectedIds);
      const results = await bulkScreenshot(ids);
      const okCount = results.filter((r) => r.ok).length;
      const failCount = results.length - okCount;
      setSnack({
        message: failCount === 0
          ? `Screenshot sent to ${okCount} agents.`
          : `Screenshot sent to ${okCount} agents (${failCount} failed).`,
        severity: failCount === 0 ? 'success' : 'error',
      });
      clearBulkSelection();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setSnack({ message: `Bulk screenshot error: ${msg}`, severity: 'error' });
    } finally {
      setBulkRunning(false);
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: 'background.paper',
          p: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search agent…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        {agents ? (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ color: 'success.main' }}>
              ● {onlineCount} online
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              · {offlineCount} offline
            </Typography>
          </Stack>
        ) : null}
      </Box>

      {bulkCount > 0 ? (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            p: 1,
            bgcolor: 'primary.dark',
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, flexGrow: 1 }}>
            {bulkCount} agent{bulkCount === 1 ? '' : 's'} selected
          </Typography>
          <Button
            size="small"
            color="inherit"
            startIcon={<PhotoCameraIcon fontSize="small" />}
            onClick={() => void handleBulkScreenshot()}
            disabled={bulkRunning}
            sx={{ textTransform: 'none', fontSize: 12 }}
          >
            {bulkRunning ? 'Sending…' : 'Screenshot all'}
          </Button>
          <Button
            size="small"
            color="inherit"
            startIcon={<ClearIcon fontSize="small" />}
            onClick={clearBulkSelection}
            disabled={bulkRunning}
            sx={{ textTransform: 'none', fontSize: 12 }}
          >
            Clear
          </Button>
        </Box>
      ) : null}

      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1.5 }}>
        {isLoading ? (
          <Stack spacing={1}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="rectangular" height={180} sx={{ borderRadius: 1 }} />
            ))}
          </Stack>
        ) : error ? (
          <Alert severity="error" sx={{ fontSize: 12 }}>
            Failed to load agents.
          </Alert>
        ) : filteredAndSorted.length === 0 ? (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', textAlign: 'center', mt: 4 }}>
            {search ? 'No agents found.' : 'No agents registered.'}
          </Typography>
        ) : (
          filteredAndSorted.map((agent) => (
            <AgentCard
              key={agent.id}
              agent={agent}
              selected={agent.id === selectedAgentId}
              bulkSelected={bulkSelectedIds.has(agent.id)}
              onClick={() => onAgentSelect(agent.id)}
              onToggleBulk={() => toggleBulkSelect(agent.id)}
              onQuickScreenshot={() => onQuickScreenshot(agent)}
              onQuickFileExplorer={() => onQuickFileExplorer(agent)}
              onQuickLivestream={() => onQuickLivestream(agent)}
            />
          ))
        )}
      </Box>

      <Snackbar
        open={snack !== null}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {snack ? (
          <Alert severity={snack.severity} onClose={() => setSnack(null)} sx={{ width: '100%' }}>
            {snack.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
